import { Amplify, API, graphqlOperation } from 'aws-amplify';
import { GraphQLResult } from '@aws-amplify/api-graphql';

import axios, { HttpStatusCode } from 'axios';

import awsconfig from '../../aws-exports';

import { createAuthorizationTokenString } from '../utils/createTokenString';
import { convertHoursIntoMiliseconds, diffMilisecondsFromNowDatetime, objectNowDate } from '../utils/dateHandler';
import { getSessionStorage, setSessionStorage } from '../utils/sessionStorageHandler';
import { isDeleteRecordInput, isMultiInput, isGraphQLErrorResponse, isGetRecordInput } from '../utils/typeGuard';
import { redirectToLogin } from '../utils/redirectToLogin';

import { sessionStorageKeyAuthorizationToken, sessionStorageKeyTokenIssuedDatetime } from '../const/commonConst';
import { QueryOutputType } from '../type/QueryType';
import { GraphQLOperaionInputType } from '../type/GraphQLOperationType';

Amplify.configure(awsconfig);

// 強制ログアウトまでの期限（24時間）
const logoutLimit = 24;

/**
 * 認証エラーかどうかを判定する
 */
const isUnauthorizedError = (err: unknown) => {
	if (!isGraphQLErrorResponse(err)) {
		console.error(err);
		return false;
	}

	const errDetail = err.errors[0];
	if (!axios.isAxiosError(errDetail.originalError) || !errDetail.originalError.response) {
		console.error(err);
		return false;
	}

	if (errDetail.originalError.response.status !== HttpStatusCode.Unauthorized) {
		console.error(err);
		return false;
	}

	return true;
};

/**
 * 認証トークンを更新する
 */
const updateAuthorizationToken = (newToken: string) => {
	try {
		setSessionStorage(sessionStorageKeyAuthorizationToken, newToken);
		setSessionStorage(sessionStorageKeyTokenIssuedDatetime, objectNowDate().toString());
		return;
	} catch (err: unknown) {
		// 失敗しても古い認証トークンは残っており、再度トークンの更新処理が実行できるため、処理を継続
		console.warn((err as Error).message);
		return;
	}
};

/**
 * リトライ用のInputを作成する
 */
const createRetryInput = (input: GraphQLOperaionInputType, oldToken: string, newToken: string) => {
	if (isDeleteRecordInput(input)) {
		const deleteRecordInput = { ...input.input, id: oldToken };
		const retryInput = { input: deleteRecordInput, expiredToken: oldToken };
		return retryInput;
	}

	if (isGetRecordInput(input)) {
		const retryInput = { ...input, id: newToken, expiredToken: oldToken };
		return retryInput;
	}

	if (isMultiInput(input)) {
		const offsegInput = { ...input.offsegInput, authorizationToken: newToken };
		const g500LiteInput = { ...input.g500LiteInput, authorizationToken: newToken };

		const retryInput = { offsegInput: offsegInput, g500LiteInput: g500LiteInput, expiredToken: oldToken };
		return retryInput;
	}

	const commonApiInput = { ...input.input, authorizationToken: newToken };
	const retryInput = { input: commonApiInput, expiredToken: oldToken };
	return retryInput;
};

/**
 * 現在日時と認証トークンの発行日時を比較し、強制ログアウトが必要かを判定する
 */
const needsLogout = (issuedDatetime: string) => {
	return diffMilisecondsFromNowDatetime(issuedDatetime) >= convertHoursIntoMiliseconds(logoutLimit);
};

/**
 *  新しい認証トークンを使用してGraphQLを再実行する
 */
const retryGraphqlOperation = async <T>(query: string, retryInput: GraphQLOperaionInputType, newToken: string) => {
	try {
		const retryResponse = (await API.graphql(graphqlOperation(query, retryInput, newToken))) as GraphQLResult<T>;

		return retryResponse.data;
	} catch (err: unknown) {
		console.error(err);

		return undefined;
	}
};

/**
 * 認証トークンの更新も含めてGraphQLを実行する
 */
export const graphqlOperationWithTokenRefresh = async <T>(
	query: string,
	input: GraphQLOperaionInputType,
	authToken: string,
): Promise<QueryOutputType<T>> => {
	try {
		const response = (await API.graphql(graphqlOperation(query, input, authToken))) as GraphQLResult<T>;

		return [false, response.data];
	} catch (err: unknown) {
		const issuedDatetime = getSessionStorage(sessionStorageKeyTokenIssuedDatetime);

		if (issuedDatetime && needsLogout(issuedDatetime)) {
			console.warn('reached logout limit');

			redirectToLogin();
		}

		if (!isUnauthorizedError(err)) {
			return [true, undefined];
		}

		// 認証エラーの場合はトークンを更新して再実施
		console.warn('authorizationToken is expired');
		const newToken = createAuthorizationTokenString();
		const retryInput = createRetryInput(input, authToken, newToken);
		const retryResponse = await retryGraphqlOperation<T>(query, retryInput, newToken);

		if (!retryResponse) {
			// 再実施でエラーが発生した場合はAPIエラーとして処理終了する
			return [true, undefined];
		}

		// 認証トークンを更新する
		updateAuthorizationToken(newToken);

		return [false, retryResponse];
	}
};
